/**
 * ComparativePanel — the L1A "Compare" stage tab (stage key `comparative`) on
 * the Project page: the §C cross-property comparison, mirroring what the
 * report's §C prints so staff see exactly what the client will.
 *
 * Layers:
 *   1. Winner strip — "Engine suggests: X" + the REAL staff recommendation
 *      control (the SHARED RecommendedPropertyPicker lifted from VerdictField —
 *      L1A shared language §4: "reuse" = mount the real component, never a
 *      lookalike; one control, one `answers.recommended_property_id` write path).
 *      A staff pick that differs from the engine's is shown calmly — the staff
 *      override legitimately wins in the report.
 *   2. Traffic-light comparison — rows (Entrances · Rooms & zones · Astro-Vastu
 *      fit · Remedy load · Overall verdict) × one column per candidate. Green /
 *      amber / red follow the canonical 3-verdict palette conventions
 *      (auoDisposition.ts → tokens.css --good/--warn/--crit ink/wash/edge, the
 *      same AA-passing recipes as the Evaluate/Astro surfaces).
 *
 * States: loading (calm status line) · error (plain-language retry) ·
 * `multi:false` (needs ≥2 evaluated properties — explains, lists whatever
 * candidates exist with their status, links the Evaluate stage) · data.
 *
 * Responsive: desktop = table; ≤860px (shell.css breakpoint via useIsMobile) =
 * stacked per-candidate cards, single column, verdict tag on the card header.
 * HARD RULES: ONE page scroll only — no inner scroll box, no horizontal-scroll
 * table on mobile (we stack instead). AVBP GREEN tokens only (tokens.css SSOT).
 *
 * Data: `resolveComparativeForProject` (Forge, in parallel) via the typed seam
 * in lib/comparative.ts — degrades to the error state while undeployed. The
 * `resolver` prop is a DI seam for the dev harness only; production always uses
 * the real seam (which itself gates the dev fixture behind `?cmpFixture=`).
 */
import { Fragment, useEffect, useState } from 'react';
import { Btn3 } from '../ui';
import { useIsMobile } from '../../lib/useIsMobile';
import {
  resolveComparativeForProject,
  type ComparativeCandidate,
  type ComparativeResolved,
  type ComparativeVerdict,
} from '../../lib/comparative';
import { RecommendedPropertyPicker } from './ReportContextCard';

/* ── presentation vocabulary (mirrors the report §C — no invented criteria) ── */

/** Colour tone — the same green/amber/red/neutral semantics as auoDisposition
 *  (Favourable = green · Remedy = amber · Not permissible/structural = red). */
type Tone = 'good' | 'warn' | 'crit' | 'mute';

const VERDICT_META: Record<ComparativeVerdict, { label: string; tone: Tone; glyph: string }> = {
  recommended: { label: 'Recommended', tone: 'good', glyph: '★' },
  acceptable: { label: 'Acceptable with effort', tone: 'warn', glyph: '≈' },
  not_recommended: { label: 'Not recommended', tone: 'crit', glyph: '✕' },
};

/** Traffic-light for a remedy/structural rollup: any structural → red; any
 *  remedy → amber; else green. (The report's §C tinting rule.) */
function loadTone(remedy: number, structural: number): Tone {
  if (structural > 0) return 'crit';
  if (remedy > 0) return 'warn';
  return 'good';
}

interface CellView {
  text: string;
  tone: Tone;
}

/* ── per-item detail view model (owner 2026-07-03: verify correctness in the
 *    table itself — every entrance, every AUO, per-member astro-fit) ── */

/** One detail line: verdict word (never colour-alone) + label + optional note. */
interface DetailItem {
  key: string;
  word: string;
  tone: Tone;
  label: string;
  note: string | null;
}

const ENTRANCE_WORD: Record<'favourable' | 'remedy' | 'structural', { word: string; tone: Tone }> = {
  favourable: { word: 'Favourable', tone: 'good' },
  remedy: { word: 'Remedy', tone: 'warn' },
  structural: { word: 'Structural', tone: 'crit' },
};
const AUO_WORD: Record<'aligned' | 'remedy' | 'structural', { word: string; tone: Tone }> = {
  aligned: { word: 'Aligned', tone: 'good' },
  remedy: { word: 'Remedy', tone: 'warn' },
  structural: { word: 'Structural', tone: 'crit' },
};

/** null → this candidate has no detail for the lens (old callable, or §E). */
type DetailFn = (c: ComparativeCandidate) => DetailItem[] | null;

const entranceDetails: DetailFn = (c) =>
  c.details
    ? c.details.entrances.map((d, i) => ({
        key: `${i}-${d.label}`,
        ...ENTRANCE_WORD[d.verdict],
        label: d.label,
        note: d.note,
      }))
    : null;

const auoDetails: DetailFn = (c) =>
  c.details
    ? c.details.auos.map((d, i) => ({
        key: `${i}-${d.label}`,
        ...AUO_WORD[d.verdict],
        label: d.criterion ? `${d.label} — ${d.criterion}` : d.label,
        note: d.note,
      }))
    : null;

const astroDetails: DetailFn = (c) =>
  c.details?.astroFit
    ? c.details.astroFit.map((d, i) => ({
        key: `${i}-${d.member}`,
        word: d.fit ? 'Matched' : 'Not matched',
        tone: d.fit ? ('good' as Tone) : ('warn' as Tone),
        label: `${d.member} — ideal ${d.ideal || '—'} · matched ${d.matched || '—'}`,
        note: null,
      }))
    : null; // astroFit detail null mirrors the summary's §E null

/** The four metric rows (the Overall-verdict row renders separately as tags).
 *  One shared row model feeds BOTH the desktop table and the mobile cards so
 *  the two layouts can never disagree. `details` powers the per-lens expander
 *  (absent on Remedy load — the contract carries no per-item remedy list). */
const METRIC_ROWS: Array<{
  key: string;
  label: string;
  cell: (c: ComparativeCandidate) => CellView;
  details?: DetailFn;
}> = [
  {
    key: 'entrances',
    label: 'Entrances (doors & openings)',
    cell: (c) => ({
      text: `${c.entrances.favourable} favourable · ${c.entrances.remedy} remedy · ${c.entrances.structural} structural`,
      tone: loadTone(c.entrances.remedy, c.entrances.structural),
    }),
    details: entranceDetails,
  },
  {
    key: 'rooms',
    label: 'Rooms & zones',
    cell: (c) => ({
      text: `${c.rooms.aligned} aligned · ${c.rooms.remedy} remedy · ${c.rooms.structural} structural`,
      tone: loadTone(c.rooms.remedy, c.rooms.structural),
    }),
    details: auoDetails,
  },
  {
    key: 'astro',
    label: 'Astro-Vastu fit',
    cell: (c) =>
      c.astroFit
        ? {
            text: `${c.astroFit.ideal} of ${c.astroFit.total} in ideal zone`,
            tone:
              c.astroFit.total > 0 && c.astroFit.ideal === c.astroFit.total
                ? 'good'
                : 'warn',
          }
        : { text: 'Resolved at curation (§E)', tone: 'mute' },
    details: astroDetails,
  },
  {
    key: 'remedy',
    label: 'Remedy load',
    cell: (c) => ({
      text: `${c.remedyLoad.total} total (${c.remedyLoad.structural} structural)`,
      tone:
        c.remedyLoad.structural > 0
          ? 'crit'
          : c.remedyLoad.total > 0
            ? 'warn'
            : 'good',
    }),
  },
];

/** A lens offers its expander only when at least one candidate actually has
 *  detail rows for it — a details-absent payload (old callable) shows no
 *  toggles anywhere (graceful degrade to the summary-only view). */
function lensHasDetail(
  row: (typeof METRIC_ROWS)[number],
  candidates: ComparativeCandidate[],
): boolean {
  if (!row.details) return false;
  return candidates.some((c) => {
    const d = row.details!(c);
    return d !== null && d.length > 0;
  });
}

/** Plain page-flow detail list — never a scroll box (owner hard rule). */
function DetailList({ items }: { items: DetailItem[] }) {
  if (items.length === 0) {
    return <p className="cmp-d-none">No items recorded.</p>;
  }
  return (
    <ul className="cmp-d-list">
      {items.map((d) => (
        <li className="cmp-d-item" key={d.key}>
          <span className={`cmp-chip ${d.tone}`}>{d.word}</span>
          <span className="cmp-d-lab">{d.label}</span>
          {d.note && <span className="cmp-d-note">{d.note}</span>}
        </li>
      ))}
    </ul>
  );
}

function VerdictTag({ verdict }: { verdict: ComparativeVerdict }) {
  const v = VERDICT_META[verdict];
  return (
    <span className={`cmp-tag ${v.tone}`}>
      <span aria-hidden>{v.glyph}</span>
      {v.label}
    </span>
  );
}

function EngineChip() {
  return <span className="cmp-engine-chip">engine pick</span>;
}

/* ── the two layouts (one shared row model) ── */

function ComparisonTable({
  candidates,
  autobestPropertyId,
  openLenses,
  onToggleLens,
}: {
  candidates: ComparativeCandidate[];
  autobestPropertyId: string | null;
  /** Which lens rows are expanded to their per-item detail. */
  openLenses: ReadonlySet<string>;
  onToggleLens: (key: string) => void;
}) {
  return (
    <div className="cmp-table-wrap">
      <table className="cmp-table" aria-label="Section C cross-property comparison — one column per candidate">
        <thead>
          <tr>
            <th scope="col" className="cmp-crit-head">Lens</th>
            {candidates.map((c) => (
              <th scope="col" key={c.propertyId} className="cmp-cand-head">
                <span className="cmp-cand-name">{c.label}</span>
                {c.propertyId === autobestPropertyId && <EngineChip />}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {METRIC_ROWS.map((row) => {
            const expandable = lensHasDetail(row, candidates);
            const open = expandable && openLenses.has(row.key);
            const detailId = `cmp-detail-${row.key}`;
            return (
              <Fragment key={row.key}>
                <tr>
                  <th scope="row" className="cmp-crit">
                    {row.label}
                    {expandable && (
                      <button
                        type="button"
                        className="cmp-lens-toggle"
                        aria-expanded={open}
                        aria-controls={detailId}
                        onClick={() => onToggleLens(row.key)}
                      >
                        {open ? 'Hide detail' : 'Show detail'}{' '}
                        <span aria-hidden>{open ? '▾' : '▸'}</span>
                      </button>
                    )}
                  </th>
                  {candidates.map((c) => {
                    const v = row.cell(c);
                    return (
                      <td key={c.propertyId} className={`cmp-cell ${v.tone}`}>
                        {v.text}
                      </td>
                    );
                  })}
                </tr>
                {open && (
                  <tr className="cmp-detail-tr" id={detailId}>
                    <th scope="row" className="cmp-crit cmp-crit-detail">
                      <span aria-hidden>↳ </span>per item
                    </th>
                    {candidates.map((c) => {
                      const items = row.details!(c);
                      return (
                        <td key={c.propertyId} className="cmp-detail-cell">
                          {items === null ? (
                            <p className="cmp-d-none">
                              {row.key === 'astro'
                                ? 'Resolved at curation (§E)'
                                : 'Detail not available for this candidate.'}
                            </p>
                          ) : (
                            <DetailList items={items} />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                )}
              </Fragment>
            );
          })}
          <tr className="cmp-verdict-row">
            <th scope="row" className="cmp-crit">Overall verdict</th>
            {candidates.map((c) => (
              <td key={c.propertyId}>
                <VerdictTag verdict={c.verdict} />
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function ComparisonCards({
  candidates,
  autobestPropertyId,
  openCards,
  onToggleCard,
}: {
  candidates: ComparativeCandidate[];
  autobestPropertyId: string | null;
  /** Which candidate cards are expanded to their per-item detail (≤860px:
   *  one toggle per card; details flow down the page — never an inner
   *  scroll box, never sideways overflow). */
  openCards: ReadonlySet<string>;
  onToggleCard: (propertyId: string) => void;
}) {
  return (
    <div className="cmp-cards">
      {candidates.map((c) => {
        const hasDetail = c.details !== null;
        const open = hasDetail && openCards.has(c.propertyId);
        const detailId = `cmp-card-detail-${c.propertyId}`;
        return (
          <section className="cmp-card" key={c.propertyId} aria-label={c.label}>
            <header className="cmp-card-h">
              <span className="cmp-cand-name">
                {c.label}
                {c.propertyId === autobestPropertyId && <EngineChip />}
              </span>
              <VerdictTag verdict={c.verdict} />
            </header>
            {METRIC_ROWS.map((row) => {
              const v = row.cell(c);
              return (
                <div className="cmp-card-row" key={row.key}>
                  <span className="cmp-lab">{row.label}</span>
                  <span className={`cmp-chip ${v.tone}`}>{v.text}</span>
                </div>
              );
            })}
            {hasDetail && (
              <div className="cmp-card-row cmp-card-detail-toggle-row">
                <button
                  type="button"
                  className="cmp-lens-toggle"
                  aria-expanded={open}
                  aria-controls={detailId}
                  onClick={() => onToggleCard(c.propertyId)}
                >
                  {open ? 'Hide detail' : 'Show detail'}{' '}
                  <span aria-hidden>{open ? '▾' : '▸'}</span>
                </button>
              </div>
            )}
            {open && (
              <div id={detailId} className="cmp-card-detail">
                {METRIC_ROWS.filter((row) => row.details).map((row) => {
                  const items = row.details!(c);
                  return (
                    <div className="cmp-card-detail-sec" key={row.key}>
                      <span className="cmp-lab">{row.label} — per item</span>
                      {items === null ? (
                        <p className="cmp-d-none">
                          {row.key === 'astro'
                            ? 'Resolved at curation (§E)'
                            : 'Detail not available for this candidate.'}
                        </p>
                      ) : (
                        <DetailList items={items} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

/* ── the panel ── */

export default function ComparativePanel({
  clientId,
  projectId,
  intakeTier,
  actor,
  onMsg,
  onGoEvaluate,
  onRecommendationChanged,
  resolver = resolveComparativeForProject,
}: {
  clientId: string | null;
  projectId: string;
  intakeTier?: string;
  actor: { uid: string | null; via: 'staff' };
  onMsg: (m: string) => void;
  /** Switches the project page to the Evaluate stage tab (goL1ATab). */
  onGoEvaluate: () => void;
  /**
   * Live echo of the saved recommended-property pick UP to the host page
   * (owner 2026-07-03: the Compare stage chip must tick without a reload).
   * ProjectDetail wires this into its stage derivation (`recommendationSet`).
   */
  onRecommendationChanged?: (propertyId: string) => void;
  /** DI seam for the dev harness ONLY — production uses the real callable seam. */
  resolver?: (clientId: string, projectId: string) => Promise<ComparativeResolved | null>;
}) {
  const isMobile = useIsMobile();
  const [data, setData] = useState<ComparativeResolved | null>(null);
  const [loading, setLoading] = useState(true);
  const [retryNonce, setRetryNonce] = useState(0);
  // The staff pick, kept live: seeded from the callable snapshot, then echoed
  // optimistically by the REAL picker's onChanged — so the divergence note
  // tracks what staff just saved without a second loader.
  const [staffPickId, setStaffPickId] = useState<string | null>(null);
  // Per-lens detail expanders (desktop table) + per-card expanders (≤860px
  // stacked cards). Chosen over one whole-table toggle: the owner verifies
  // lens-by-lens, and expanding only the lens under check keeps the summary
  // calm while its tally sits directly above its own detail rows.
  const [openLenses, setOpenLenses] = useState<ReadonlySet<string>>(new Set());
  const [openCards, setOpenCards] = useState<ReadonlySet<string>>(new Set());
  const toggleLens = (key: string) =>
    setOpenLenses((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const toggleCard = (propertyId: string) =>
    setOpenCards((prev) => {
      const next = new Set(prev);
      if (next.has(propertyId)) next.delete(propertyId);
      else next.add(propertyId);
      return next;
    });

  useEffect(() => {
    if (!clientId) {
      setData(null);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    (async () => {
      const resolved = await resolver(clientId, projectId);
      if (!alive) return;
      setData(resolved);
      setStaffPickId(resolved?.recommendedPropertyId ?? null);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [clientId, projectId, retryNonce, resolver]);

  // ── no client linked — same calm guidance the Verdict screen uses ─────────
  if (!clientId) {
    return (
      <div className="cmp-panel" data-testid="cmp-panel">
        <div className="cmp-empty">
          Link a client to this project first — the comparison reads the client's
          evaluated candidate properties.
        </div>
      </div>
    );
  }

  // ── loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="cmp-panel" data-testid="cmp-panel">
        <div className="cmp-empty" role="status" aria-live="polite">
          Weighing the evaluated properties side-by-side…
        </div>
      </div>
    );
  }

  // ── error / not yet available — plain language + retry ────────────────────
  if (!data) {
    return (
      <div className="cmp-panel" data-testid="cmp-panel">
        <div className="cmp-empty">
          <p style={{ margin: '0 0 12px' }}>
            The comparison couldn't be loaded — it may not be available for this
            project yet. Nothing is lost; try again in a moment.
          </p>
          <Btn3 intent="ink" size="sm" onClick={() => setRetryNonce((n) => n + 1)}>
            Try again
          </Btn3>
        </div>
      </div>
    );
  }

  // ── BACKEND compute failure — checked BEFORE the multi:false branch ────────
  // Owner-reported (VP678, 2026-07-03): the callable returned
  // `{ multi:false, candidates:[], error:"comparative compute failed: …" }`
  // and the tab showed the needs-2+ guidance — blaming the project's data for
  // an engine crash. A non-empty `error` ALWAYS renders the error state; the
  // needs-2+ state is legitimate only when the compute genuinely succeeded
  // with <2 candidates and no error.
  if (data.error) {
    return (
      <div className="cmp-panel" data-testid="cmp-panel">
        <div className="cmp-empty" data-testid="cmp-engine-error">
          {/* Copy honesty (Vera F4): no automatic notification fires — the
              copy must not claim one. Words are contracts. */}
          <p style={{ margin: '0 0 12px' }}>
            Something went wrong computing the comparison on our side — try
            again. If it keeps happening, tell the team.
          </p>
          <Btn3 intent="ink" size="sm" onClick={() => setRetryNonce((n) => n + 1)}>
            Try again
          </Btn3>
        </div>
      </div>
    );
  }

  const { candidates, autobestPropertyId } = data;
  const byId = new Map(candidates.map((c) => [c.propertyId, c] as const));
  const engineCand = autobestPropertyId ? byId.get(autobestPropertyId) ?? null : null;
  // In sample mode the divergence note keys off FIXTURE data only (staffPickId
  // is seeded from the sample payload and there is no real control to move it).
  const staffDiffers = Boolean(
    autobestPropertyId && staffPickId && staffPickId !== autobestPropertyId,
  );

  // Sample mode (owner incident 2026-07-03): the `?cmpFixture=1` payload is
  // stamped isFixture — it MUST self-identify (banner) and MUST NOT sit next
  // to real controls (a real picker showing real properties beside sample
  // numbers is exactly the confusion that hit the owner).
  const isSample = data.isFixture === true;
  const sampleBanner = isSample ? (
    <div className="cmp-sample-banner" role="note" data-testid="cmp-sample-banner">
      <span className="cmp-warn-ic" aria-hidden>⚠</span>
      <p>
        <b>Sample data — design preview only.</b> This is not this project's data;
        the real comparison assembles when 2+ properties are evaluated.
      </p>
    </div>
  ) : null;

  // ── not enough evaluated candidates (multi:false) ──────────────────────────
  if (!data.multi) {
    return (
      <div className="cmp-panel" data-testid="cmp-panel">
        {sampleBanner}
        <div className="cmp-hint">
          <span className="cmp-mk" aria-hidden>✦</span>
          {/* Wording per owner ruling 2026-07-03: AUOs are NOT implied as
              required — a vacant plot has none; boundary + entrance alone is
              a complete candidate. */}
          <p style={{ margin: 0 }}>
            <b>Compare needs at least 2 evaluated properties.</b> Evaluate each
            candidate in the <b>Evaluate</b> stage — trace the boundary and mark the
            entrance, marking AUOs where the property has them — and once a second
            candidate is report-ready, the §C side-by-side assembles here
            automatically.
          </p>
        </div>
        <div>
          <Btn3 intent="grn" size="sm" onClick={onGoEvaluate}>
            Go to Evaluate →
          </Btn3>
        </div>
        {candidates.length > 0 && (
          <>
            <p className="cmp-note">
              {candidates.length === 1
                ? 'One candidate is evaluated so far — its status:'
                : 'Candidates so far — their status:'}
            </p>
            <ComparisonCards
              candidates={candidates}
              autobestPropertyId={autobestPropertyId}
              openCards={openCards}
              onToggleCard={toggleCard}
            />
          </>
        )}
      </div>
    );
  }

  // ── the §C comparison ──────────────────────────────────────────────────────
  return (
    <div className="cmp-panel" data-testid="cmp-panel">
      {sampleBanner}
      {/* 1 · WINNER STRIP — engine suggestion + the staff control */}
      <div className="cmp-winner">
        <div className="cmp-win-cell">
          <span className="cmp-lab">Engine suggests</span>
          <span className="cmp-win-name">
            {engineCand?.label ?? autobestPropertyId ?? '— no engine pick yet —'}
          </span>
          {staffDiffers && (
            <p className="cmp-win-note">
              The staff pick differs from the engine's suggestion — that's fine: the
              staff recommendation is what the report prints.
            </p>
          )}
        </div>
        <div className="cmp-win-cell">
          {isSample ? (
            /* Sample mode — a DISABLED stand-in echoing the fixture's staff
               pick. The real RecommendedPropertyPicker is deliberately NOT
               mounted: it lists the project's REAL properties and saves REAL
               data, which must never sit beside sample numbers. */
            <div data-testid="cmp-sample-picker">
              <label className="cmp-sample-lab" htmlFor="cmp-sample-select">
                Recommended property (sample)
              </label>
              <select
                id="cmp-sample-select"
                className="cmp-sample-select"
                disabled
                defaultValue={data.recommendedPropertyId ?? ''}
                aria-label="Recommended property — sample preview, disabled"
              >
                <option value="">— set at curation —</option>
                {candidates.map((c) => (
                  <option key={c.propertyId} value={c.propertyId}>
                    {c.label}
                  </option>
                ))}
              </select>
              <p className="cmp-note" style={{ marginTop: 6 }}>
                Sample control — the real recommendation control appears with this
                project's real data.
              </p>
            </div>
          ) : (
            /* The SHARED real control (also on the Verdict screen) — reads/
               writes answers.recommended_property_id via saveRecommendedProperty. */
            <div data-testid="cmp-real-picker">
              <RecommendedPropertyPicker
                clientId={clientId}
                projectId={projectId}
                intakeTier={intakeTier}
                actor={actor}
                onMsg={onMsg}
                onChanged={(id) => {
                  setStaffPickId(id || null);
                  onRecommendationChanged?.(id);
                }}
                helper="Confirm or override the engine's suggestion — the staff pick is what the report prints (§C/§D)."
              />
            </div>
          )}
        </div>
      </div>

      {/* 2 · TRAFFIC-LIGHT COMPARISON — table on desktop, stacked cards ≤860px */}
      {isMobile ? (
        <ComparisonCards
          candidates={candidates}
          autobestPropertyId={autobestPropertyId}
          openCards={openCards}
          onToggleCard={toggleCard}
        />
      ) : (
        <ComparisonTable
          candidates={candidates}
          autobestPropertyId={autobestPropertyId}
          openLenses={openLenses}
          onToggleLens={toggleLens}
        />
      )}

      <p className="cmp-note">
        Mirrors the report's §C — what you see here is what prints. Pending
        candidates join once they're report-ready in <b>Evaluate</b>.
      </p>
    </div>
  );
}
